

























































































import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { ReferralAPI } from '@/api/backend-client'
import { ReferralStats } from '@/api/model/referral-stats'
import { PipelineAPI } from '@/api/backend-client'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { PipelinePhaseCategory, ReferralStatus } from '@/api/types'
import { getColorForPhaseCategory } from '@/helpers/color'
import { namespace } from 'vuex-class'
const referrals = namespace('referrals')

@Component({
    methods: {
        getColorForPhaseCategory,
    },
})
export default class ReferralsBlock extends Vue {
    @referrals.Action applyFilters: (ReferralsStateFilters) => Promise<void>
    loading: boolean = true
    referralStats: ReferralStats = null
    pipelines: Pipeline[] = []
    pipeline: string = null
    view: string = 'user'
    @Ref() tabs: HTMLDivElement
    tabsObserver: ResizeObserver = null
    tabsScrollable: boolean = false
    tabsArrowLeftEnabled: boolean = false
    tabsArrowRightEnabled: boolean = true

    async mounted(): Promise<void> {
        this.view = this.$currentUser.defaultDashboardView
        await this.loadPipelines()
        await this.loadReferralStats()

        this.tabsObserver = new ResizeObserver(() => {
            this.updateTabs()
        })

        this.addEventListeners()
    }

    async beforeDestroy(): Promise<void> {
        this.removeEventListeners()
    }

    addEventListeners(): void {
        if (!this.tabs) {
            return
        }

        // Add event listener for tab scroll and update tabs
        this.tabs.addEventListener('scroll', this.updateTabs)
        this.updateTabs()

        // Add event listener for tab resize
        this.tabsObserver.observe(this.tabs)
    }

    removeEventListeners(): void {
        if (!this.tabs) {
            return
        }

        // Remove event listener for tab scroll
        this.tabs.removeEventListener('scroll', this.updateTabs)

        // Remove event listener for tab resize
        this.tabsObserver.unobserve(this.tabs)
    }

    get availablePipelines(): Pipeline[] {
        return this.pipelines
    }

    get availablePipelinesOptions() {
        const options = this.availablePipelines
            .map(pipeline => ({
                label: pipeline.name,
                value: pipeline.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        options.unshift({
            label: this.$t('dashboard.referrals.pipeline.all').toString(),
            value: null,
        })

        return options
    }

    get selectedPipeline(): Pipeline {
        return this.getPipeline(this.pipeline)
    }

    get availablePhases(): PipelinePhase[] {
        if (!this.selectedPipeline) {
            return []
        }

        return this.selectedPipeline.activePhases
    }

    get availablePhaseCategories(): PipelinePhaseCategory[] {
        return [
            PipelinePhaseCategory.ENTRY,
            PipelinePhaseCategory.APPROACH,
            PipelinePhaseCategory.DECISION,
        ]
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`dashboard.referrals.view.${availableView}`),
                value: availableView,
            }))
    }

    @Watch('view')
    reloadReferralStatus() {
        this.loadReferralStats()
    }

    async loadPipelines(): Promise<void> {
        this.pipelines = (await PipelineAPI.getAll({ company: this.$currentCompany.resourceIdentifier}))
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(pipeline => pipeline.active)
    }

    async loadReferralStats(): Promise<void> {
        this.loading = true
        this.referralStats = await ReferralAPI.getCounts(
            null,
            null,
            null,
            null,
            {
                view: `${this.view}_in`,
            },
        )
        this.loading = false
    }

    getCountForPipeline(id: string): number {
        return this.referralStats.countForPipeline(id)
    }

    getCountForPhase(id: string): number {
        return this.referralStats.countForPhase(id)
    }

    getCountForPhaseCategory(category: string): number {
        return this.referralStats.countForCategory(category)
    }

    getPipeline(id: string): Pipeline {
        return this.availablePipelines.find(v => v.id === id)
    }

    canViewPotentialOrderValue(id: string): boolean {
        if (!id) {
            return this.availablePipelines.findIndex(p => p.canViewPotentialOrderValue(this.$currentUser)) >= 0
        }

        return this.getPipeline(id).canViewPotentialOrderValue(this.$currentUser)
    }

    getPotentialOrderValueForPipeline(id: string): number {
        if (!id) {
            let potentialOrderValue = 0
            this.availablePipelines.forEach(p => {
                potentialOrderValue += this.getPotentialOrderValueForPipeline(p.id)
            })

            return potentialOrderValue
        }

        return this.referralStats.potentialOrderValueForPipeline(id)
    }

    getPotentialOrderValueForPhase(pipelineId: string, phaseId: string): number {
        return this.referralStats.potentialOrderValueForPhase(phaseId)
    }

    getExpectedOrderValueForPhase(pipelineId: string, phaseId: string): number {
        return this.getPotentialOrderValueForPhase(pipelineId, phaseId)
            * this.getConversionPercentageForPipeline(pipelineId)
            / 100
    }

    getConversionPercentageForPipeline(pipelineId: string): number {
        return this.getPipeline(pipelineId).conversionPercentage
    }

    async goToList(filters: object) {
        filters = Object.assign({}, {
            dateRangeFilter: 'all',
            direction: 'in',
            view: this.view,
            statusFilter: ReferralStatus.OPEN,
            typeFilter: [],
            pipelineFilter: this.pipeline,
            phaseFilter: null,
            phaseCategoryFilter: null,
        }, filters)

        await this.applyFilters(filters)
        await this.$router.push({name: 'tasks_all'})
    }

    updateTabs() {
        if (!this.tabs) {
            return
        }

        this.tabsScrollable = this.tabs.scrollWidth > this.tabs.clientWidth
        this.tabsArrowLeftEnabled = this.tabs.scrollLeft > 0
        this.tabsArrowRightEnabled = this.tabs.scrollLeft < (this.tabs.scrollWidth - this.tabs.clientWidth)
    }

    scrollLeft(): void {
        if (!this.tabsArrowLeftEnabled) {
            return
        }

        this.tabs.scrollTo({
            behavior: 'smooth',
            left: this.tabs.scrollLeft - this.tabs.clientWidth / 4,
        })
    }

    scrollRight(): void {
        if (!this.tabsArrowRightEnabled) {
            return
        }

        this.tabs.scrollTo({
            behavior: 'smooth',
            left: this.tabs.scrollLeft + this.tabs.clientWidth / 4,
        })
    }
}
